import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import {
   BrickCategoryTabSelectedAction,
   BrickDescriptionSearchChangedAction,
   BrickDescriptionSelectedForDetailsAction,
   CloseAddBrickDialogAction,
   OpenAddBrickDialogAction,
   ToggleAddBrickDialog,
} from './add-new-brick-dialog.actions';
import { BrickCategory } from '../../../samwebDescription/samweb/brickCategoryType';
import { ITabItem } from '../../../samwebDescription/samweb/ITabItem';
import _ from 'lodash';
import { BrickDefinitionState } from '../brick-wall/brick-definition/brick-definition.state';
import { BrickDefinition } from '../../../samwebDescription/samweb/brickDefinition';
import { BrickInstancesSelectors } from '../brick-wall/brick-instances/brick-instances.selectors';
import { Injectable } from '@angular/core';

export interface AddNewBrickDialogStateModel {
   isOpen: boolean;
   brickSearchText: string;
   selectedTabIndex: number;
   selectedBrickDefinitionId: string;
}

@State<AddNewBrickDialogStateModel>({
   name: 'addNewBrickDialog',
   defaults: {
      isOpen: false,
      brickSearchText: '',
      selectedTabIndex: 0,
      selectedBrickDefinitionId: null,
   },
})
@Injectable()
export class AddNewBrickDialogState {
   constructor() {}

   @Selector([AddNewBrickDialogState, BrickInstancesSelectors.isBrickWallEditable])
   public static isBrickMenuOpen(state: AddNewBrickDialogStateModel, isBrickWallEditModeOn: boolean) {
      return state.isOpen && isBrickWallEditModeOn;
   }

   @Selector([BrickDefinitionState.brickDefinitionsAsArray, BrickInstancesSelectors.isBrickUsageAllowedFn])
   public static brickCategoriesWithBricksFn(
      brickDefinitions: BrickDefinition[],
      isBrickUsageAllowedFn: (BrickDefinition) => boolean
   ): (store: Store) => BrickCategory[] {
      return (store: Store) => {
         const categories: BrickCategory[] = _(brickDefinitions)
            .filter((x) => !x.disableAddingByUser && isBrickUsageAllowedFn(x))
            .map((x) => x.brickCategory)
            .sortBy((x) => x.sortOrder)
            .uniq()
            .value();
         return categories;
      };
   }

   @Selector([AddNewBrickDialogState])
   public static brickDefinitionSearchInput(state: AddNewBrickDialogStateModel) {
      return state.brickSearchText;
   }

   @Selector([AddNewBrickDialogState.brickCategoriesWithBricksFn])
   public static brickCategoriesTabsFn(brickCategoriesFn): (store: Store) => ITabItem<BrickCategory>[] {
      return (store: Store) => {
         const tabs: ITabItem<BrickCategory>[] = _(brickCategoriesFn(store))
            .map((category) => {
               return <ITabItem<BrickCategory>>{
                  titleTranslationKey: category.translationKey,
                  itemRepresentationValue: category,
               };
            })
            .value();

         // unshift adds an element to the beginning of an array
         tabs.unshift(<ITabItem<BrickCategory>>{
            titleTranslationKey: 'brick.brick-management.category_all',
            itemRepresentationValue: null,
         });
         return tabs;
      };
   }

   @Selector([AddNewBrickDialogState])
   public static selectedTabIndex(state: AddNewBrickDialogStateModel) {
      return state.selectedTabIndex;
   }

   @Selector([AddNewBrickDialogState.selectedTabIndex, AddNewBrickDialogState.brickCategoriesTabsFn])
   public static selectedCategoryTabFn(
      selectedTabIndex,
      brickCategoriesTabsFn: (store: Store) => ITabItem<BrickCategory>[]
   ): (store: Store) => ITabItem<BrickCategory> {
      return (store: Store) => brickCategoriesTabsFn(store)[selectedTabIndex];
   }

   @Selector([AddNewBrickDialogState])
   public static selectedBrickDefinitionForDetails(state: AddNewBrickDialogStateModel) {
      return state.selectedBrickDefinitionId;
   }

   @Action(OpenAddBrickDialogAction)
   public openBrickMenu(ctx: StateContext<AddNewBrickDialogStateModel>, action: OpenAddBrickDialogAction) {
      ctx.patchState({
         isOpen: true,
         brickSearchText: '',
      });
   }

   @Action(CloseAddBrickDialogAction)
   public closeBrickMenu(ctx: StateContext<AddNewBrickDialogStateModel>, action: CloseAddBrickDialogAction) {
      ctx.patchState({
         isOpen: false,
         brickSearchText: '',
      });
   }

   @Action(ToggleAddBrickDialog)
   public toggleBrickMenu(ctx: StateContext<AddNewBrickDialogStateModel>, action: ToggleAddBrickDialog) {
      ctx.patchState({
         isOpen: !ctx.getState().isOpen,
         brickSearchText: '',
      });
   }

   @Action(BrickCategoryTabSelectedAction)
   public brickCategoryTabSelected(ctx: StateContext<AddNewBrickDialogStateModel>, action: BrickCategoryTabSelectedAction) {
      ctx.patchState({
         selectedTabIndex: action.tabIndex,
      });
   }

   @Action(BrickDescriptionSearchChangedAction)
   public brickDescriptionSearchChanged(ctx: StateContext<AddNewBrickDialogStateModel>, action: BrickDescriptionSearchChangedAction) {
      ctx.patchState({
         brickSearchText: action.searchText,
      });
   }

   @Action(BrickDescriptionSelectedForDetailsAction)
   public brickDescriptionSelectedForDetails(ctx: StateContext<AddNewBrickDialogStateModel>, action: BrickDescriptionSelectedForDetailsAction) {
      ctx.patchState({
         selectedBrickDefinitionId: action.brickDefinitionId,
      });
   }
}
